import React, { useState } from 'react';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { AlertCircle, ArrowRight, TrendingUp, TrendingDown, Equal, ChevronRight } from 'lucide-react';
import DefaultLayout from '../../layout/DefaultLayout';
import { useAuth } from 'wasp/client/auth';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTranslation } from '../../../i18n/hooks';

type Metric = 'sales' | 'clicks' | 'views';

interface MetricData {
  label: string;
  control: number;
  variant: number;
  improvement: string;
}

interface DailyData {
  date: string;
  controlSales: number;
  variantSales: number;
  controlViews: number;
  variantViews: number;
  controlClicks: number;
  variantClicks: number;
}

interface VariantData {
  name: string;
  content: string;
  views: number;
  clicks: number;
  sales: number;
}

interface TestData {
  id: number;
  name: string;
  startDate: string;
  status: 'running' | 'conclusive';
  significance: number;
  variants: {
    control: VariantData;
    variant: VariantData;
  };
  dailyData: DailyData[];
}

const AdminOptimizationDashboardPage = () => {
  const { data: user, isLoading } = useAuth();
  const [selectedMetric, setSelectedMetric] = useState<Metric>('sales');
  const { t } = useTranslation();

  // Mock data - replace with real data in production
  const runningTests: TestData[] = [
    {
      id: 1,
      name: t("optimizationDashboard.test_benefit_focused_titles"),
      startDate: "2024-01-15",
      status: "running",
      significance: 0.92,
      variants: {
        control: {
          name: t("optimizationDashboard.variant_original_title"),
          content: t("optimizationDashboard.content_memory_foam_shoes"),
          views: 2500,
          clicks: 180,
          sales: 45
        },
        variant: {
          name: t("optimizationDashboard.variant_benefit_focused_title"),
          content: t("optimizationDashboard.content_never_sore_feet"),
          views: 2480,
          clicks: 225,
          sales: 62
        }
      },
      dailyData: [
        { date: '2024-01-15', controlSales: 8, variantSales: 12, controlViews: 450, variantViews: 445, controlClicks: 35, variantClicks: 42 },
        { date: '2024-01-16', controlSales: 10, variantSales: 15, controlViews: 520, variantViews: 510, controlClicks: 40, variantClicks: 48 },
        { date: '2024-01-17', controlSales: 9, variantSales: 14, controlViews: 480, variantViews: 475, controlClicks: 38, variantClicks: 45 },
        { date: '2024-01-18', controlSales: 11, variantSales: 16, controlViews: 510, variantViews: 505, controlClicks: 42, variantClicks: 50 },
        { date: '2024-01-19', controlSales: 12, variantSales: 18, controlViews: 530, variantViews: 525, controlClicks: 44, variantClicks: 53 },
        { date: '2024-01-20', controlSales: 10, variantSales: 16, controlViews: 490, variantViews: 485, controlClicks: 39, variantClicks: 47 },
        { date: '2024-01-21', controlSales: 7, variantSales: 13, controlViews: 420, variantViews: 415, controlClicks: 32, variantClicks: 40 }
      ]
    },
    {
      id: 2,
      name: t("optimizationDashboard.test_description_format"),
      startDate: "2024-01-10",
      status: "conclusive",
      significance: 0.97,
      variants: {
        control: {
          name: t("optimizationDashboard.variant_bullet_points"),
          content: t("optimizationDashboard.content_bullet_points"),
          views: 3200,
          clicks: 245,
          sales: 58
        },
        variant: {
          name: t("optimizationDashboard.variant_narrative_style"),
          content: t("optimizationDashboard.content_narrative_style"),
          views: 3180,
          clicks: 290,
          sales: 82
        }
      },
      dailyData: [
        { date: '2024-01-10', controlSales: 12, variantSales: 18, controlViews: 650, variantViews: 645, controlClicks: 45, variantClicks: 58 },
        { date: '2024-01-11', controlSales: 15, variantSales: 20, controlViews: 620, variantViews: 630, controlClicks: 50, variantClicks: 62 }
      ]
    }
  ];

  const getMetricData = (test: TestData) => {
    const metrics: Record<Metric, MetricData> = {
      sales: {
        label: t('optimizationDashboard.sales'),
        control: test.variants.control.sales,
        variant: test.variants.variant.sales,
        improvement: calculateImprovement(test.variants.control.sales, test.variants.variant.sales)
      },
      clicks: {
        label: t('optimizationDashboard.clicks'),
        control: test.variants.control.clicks,
        variant: test.variants.variant.clicks,
        improvement: calculateImprovement(test.variants.control.clicks, test.variants.variant.clicks)
      },
      views: {
        label: t('optimizationDashboard.views'),
        control: test.variants.control.views,
        variant: test.variants.variant.views,
        improvement: calculateImprovement(test.variants.control.views, test.variants.variant.views)
      }
    };
    return metrics;
  };

  const getSignificanceIndicator = (significance: number) => {
    if (significance >= 0.95) {
      return {
        icon: <TrendingUp className="h-5 w-5 text-green-500" />,
        message: t('optimizationDashboard.high_confidence_message'),
        color: "text-green-700 bg-green-50"
      };
    } else if (significance >= 0.85) {
      return {
        icon: <TrendingUp className="h-5 w-5 text-yellow-500" />,
        message: t('optimizationDashboard.medium_confidence_message'),
        color: "text-yellow-700 bg-yellow-50"
      };
    } else {
      return {
        icon: <Equal className="h-5 w-5 text-blue-500" />,
        message: t('optimizationDashboard.low_confidence_message'),
        color: "text-blue-700 bg-blue-50"
      };
    }
  };

  const calculateImprovement = (control: number, variant: number): string => {
    const improvement = ((variant - control) / control * 100).toFixed(1);
    return improvement;
  };

  if (isLoading) return null;


  if(!user){
    return <></>
  }

  return (
    <DefaultLayout user={user}>
      <div className="rounded-sm border border-stroke bg-white px-5 pb-2.5 pt-6 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {t('optimizationDashboard.running_tests')}
              </h1>
              <p className="text-gray-500 dark:text-gray-400">
                {t('optimizationDashboard.active_tests_description')}
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {runningTests.map((test) => {
              const metrics = getMetricData(test);

              return (
                <Card key={test.id} className="dark:bg-boxdark">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <CardTitle className="text-xl dark:text-white">{test.name}</CardTitle>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline">{t('optimizationDashboard.control')}: {test.variants.control.content}</Badge>
                          <Badge variant="outline">{t('optimizationDashboard.test')}: {test.variants.variant.content}</Badge>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
  {(() => {
    const sigIndicator = getSignificanceIndicator(test.significance);
    return (
      <>
<Badge className={`flex items-center gap-2 py-1.5 font-normal ${sigIndicator.color}`}>
  {sigIndicator.icon}
  <span>{sigIndicator.message}</span>
</Badge>
        {test.significance >= 0.95 && (
          <Button 
            variant="default" 
            size="sm"
            className="flex items-center justify-center gap-1.5 bg-green-600 text-white hover:bg-green-700 font-medium"
          >
            <span>{t('optimizationDashboard.deploy_winning_variant')}</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </>
    );
  })()}
</div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                  <div className="space-y-2"> 
                    {/* Metric Selector */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-gray-500">{t('optimizationDashboard.showing')}:</span>
                      <div className="flex gap-1">
                        {[
                          { value: 'sales', label: t('optimizationDashboard.sales') },
                          { value: 'clicks', label: t('optimizationDashboard.clicks') },
                          { value: 'views', label: t('optimizationDashboard.views') }
                        ].map((metric) => (
                          <Button
                            key={metric.value}
                            variant={selectedMetric === metric.value ? "default" : "outline"}
                            className="px-3 py-1 h-8"
                            onClick={() => setSelectedMetric(metric.value as Metric)}
                          >
                            {metric.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Performance Graph */}
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={test.dailyData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <XAxis
                            dataKey="date"
                            tickFormatter={(date) => {
                              const d = new Date(date);
                              return `${d.toLocaleString('en-US', { month: 'short' })}-${d.getDate()}`;
                            }}
                            stroke="#94a3b8"
                            tickLine={false}
                            axisLine={false}
                            dy={10}
                          />
                          <YAxis
                            stroke="#94a3b8"
                            tickLine={false}
                            axisLine={false}
                            dx={-10}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                              padding: '12px'
                            }}
                            labelStyle={{
                              color: '#64748b',
                              marginBottom: '4px'
                            }}
                          />
                          <Line
                            type="basis"
                            dataKey={`control${selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}`}
                            stroke="#6366f1"
                            strokeWidth={3}
                            name={t('optimizationDashboard.control')}
                            dot={false}
                            activeDot={{ r: 6, strokeWidth: 0 }}
                          />
                          <Line
                            type="basis"
                            dataKey={`variant${selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}`}
                            stroke="#10b981"
                            strokeWidth={3}
                            name={t('optimizationDashboard.test')}
                            dot={false}
                            activeDot={{ r: 6, strokeWidth: 0 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>                     </div>


                    {/* Comparison Table */}
                    <table className="w-full text-sm">
                      <thead className="border-b dark:border-gray-700">
                        <tr>
                          <th className="text-left py-2">{t('optimizationDashboard.metric')}</th>
                          <th className="text-right py-2">{t('optimizationDashboard.control')}</th>
                          <th className="text-right py-2">{t('optimizationDashboard.test')}</th>
                          <th className="text-right py-2">{t('optimizationDashboard.improvement')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(metrics).map(([key, data]) => (
                          <tr key={key} className="border-b dark:border-gray-700">
                            <td className="py-3">{data.label}</td>
                            <td className="text-right">{data.control}</td>
                            <td className="text-right">{data.variant}</td>
                            <td className="text-right">
                              <div className="flex items-center justify-end gap-1 text-emerald-600">
                                <TrendingUp className="h-4 w-4" />
                                {data.improvement}%
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default AdminOptimizationDashboardPage;